import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Callout,
  Icon,
  Progress,
  Rule,
  SectionHeader,
  Spinner,
  Stamp,
  Stat,
  Tag,
} from "../ui";
import { useExtractionsSummary } from "../hooks/useExtractionsSummary";
import { useJobPolling } from "../hooks/useJobPolling";
import { useSources } from "../hooks/useSources";
import { useBuildReferences } from "../hooks/useReferences";
import { useStartExtraction } from "../hooks/useStartExtraction";
import { LABELS_16 } from "../data/mock";
import type { ExtractJobMetadata, Job, Project, Source } from "../types";

export interface Step4ExtractionProps {
  project: Project;
  mock: boolean;
  onAdvance: (n: number) => void;
}

interface Stage {
  label: string;
  pct: number;
}

interface GroupedError {
  key: string;
  stage: string;
  count: number;
  items: Array<Record<string, unknown>>;
}

interface PendingSource {
  source_id?: string;
  citation_number?: number | null;
  title?: string | null;
  url?: string;
}

interface ExtractionConflict {
  reason: string;
  sources: PendingSource[];
}

const STAGES: Stage[] = [
  { label: "Download das fontes", pct: 15 },
  { label: "Extracao PDF/HTML", pct: 35 },
  { label: "Normalizacao e chunking", pct: 50 },
  { label: "Pre-filtro lexico", pct: 65 },
  { label: "Classificacao LLM", pct: 95 },
];
const ALL_LABEL_KEYS = LABELS_16.map((label) => label.key);

function asRecord(item: unknown): Record<string, unknown> | null {
  if (item !== null && typeof item === "object") return item as Record<string, unknown>;
  return null;
}

function isStructuredConflict(detail: unknown): detail is Record<string, unknown> {
  return (
    detail !== null &&
    typeof detail === "object" &&
    (Object.prototype.hasOwnProperty.call(detail, "reason") ||
      Object.prototype.hasOwnProperty.call(detail, "message") ||
      Object.prototype.hasOwnProperty.call(detail, "missing_references") ||
      Object.prototype.hasOwnProperty.call(detail, "missing_sources") ||
      Object.prototype.hasOwnProperty.call(detail, "sources") ||
      Object.prototype.hasOwnProperty.call(detail, "pending_sources") ||
      Object.prototype.hasOwnProperty.call(detail, "missing_reference_ids") ||
      Object.prototype.hasOwnProperty.call(detail, "source_ids") ||
      Object.prototype.hasOwnProperty.call(detail, "items"))
  );
}

function errorDetail(error: unknown): string {
  if (
    isAxiosError(error) &&
    error.response?.status === 409 &&
    isStructuredConflict(error.response?.data?.detail)
  ) {
    const detail = error.response.data.detail;
    if (typeof detail.message === "string") return detail.message;
    return "Algumas fontes selecionadas ainda nao possuem referencia ABNT.";
  }
  const responseData = asRecord(isAxiosError(error) ? error.response?.data : undefined);
  if (responseData && typeof responseData.detail === "string") return responseData.detail;
  return "Nao foi possivel iniciar a extracao.";
}

function normalizeSource(item: unknown): PendingSource | null {
  if (typeof item === "string") {
    return { source_id: item };
  }
  const record = asRecord(item);
  if (!record) return null;
  const sourceId =
    typeof record.source_id === "string"
      ? record.source_id
      : typeof record.id === "string"
        ? record.id
        : undefined;
  const citation = typeof record.citation_number === "number" ? record.citation_number : null;
  const title =
    typeof record.title === "string"
      ? record.title
      : typeof record.source_title === "string"
        ? record.source_title
        : undefined;
  const url = typeof record.url === "string" ? record.url : undefined;
  return { source_id: sourceId, citation_number: citation, title, url };
}

function parseStructuredConflict(error: unknown): ExtractionConflict | null {
  if (!isAxiosError(error)) return null;
  if (error.response?.status !== 409) return null;
  const detail = error.response.data?.detail;

  if (isStructuredConflict(detail)) {
    const reason =
      typeof detail.message === "string"
        ? detail.message
        : typeof detail.reason === "string"
          ? detail.reason
          : "Algumas fontes selecionadas nao possuem referencia ABNT.";

    const candidates = [
      detail.missing_references,
      detail.sources,
      detail.missing_sources,
      detail.pending_sources,
      detail.missing_reference_ids,
      detail.source_ids,
      detail.items,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        const normalized = candidate
          .map(normalizeSource)
          .filter((entry): entry is PendingSource => entry !== null);
        if (normalized.length > 0) {
          return { reason, sources: normalized };
        }
      }
    }

    return { reason, sources: [] };
  }

  if (typeof detail === "string") {
    return { reason: detail, sources: [] };
  }

  return { reason: "Nao foi possivel iniciar a extracao.", sources: [] };
}

function metadata(job: Job | undefined): ExtractJobMetadata {
  return (job?.metadata ?? {}) as ExtractJobMetadata;
}

function selectedSourceMap(sources: Source[] | undefined) {
  const map = new Map<
    string,
    {
      title: string | null;
      citation_number: number | null;
      url: string;
      abnt_reference: string | null;
    }
  >();
  if (!sources) return map;
  for (const source of sources) {
    map.set(source.id, {
      title: source.title,
      citation_number: source.citation_number,
      url: source.url,
      abnt_reference: source.abnt_reference,
    });
  }
  return map;
}

function groupErrors(job: Job | undefined): GroupedError[] {
  const entries = job?.error_log ?? [];
  const grouped = new Map<string, GroupedError>();
  for (const item of entries) {
    const stage = typeof item.stage === "string" ? item.stage : "unknown";
    const source = typeof item.source_id === "string" ? item.source_id : "sem-fonte";
    const key = `${stage}:${source}`;
    const current = grouped.get(key) ?? { key, stage, count: 0, items: [] };
    current.count += 1;
    current.items.push(item);
    grouped.set(key, current);
  }
  return Array.from(grouped.values());
}

export function Step4Extraction({ project, onAdvance }: Step4ExtractionProps) {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ExtractionConflict | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<string[]>(ALL_LABEL_KEYS);
  const selectedSources = useSources(project.id, { selected: true });
  const startExtraction = useStartExtraction(project.id);
  const buildReferences = useBuildReferences(project.id);
  const job = useJobPolling(jobId);

  const terminalSuccess = job.data?.status === "success";
  const terminalFailed = job.data?.status === "failed" && !startExtraction.isPending;
  const summaryEnabled = terminalSuccess || project.current_step > 4;
  const summary = useExtractionsSummary(project.id, summaryEnabled);
  const jobMeta = metadata(job.data);
  const selectedCount = selectedSources.data?.length ?? 0;
  const summaryRows = summary.data ?? [];
  const totals = useMemo(
    () =>
      summaryRows.reduce(
        (acc, row) => ({
          total: acc.total + row.total,
          approved: acc.approved + row.approved,
          pending: acc.pending + row.pending,
          rejected: acc.rejected + row.rejected,
        }),
        { total: 0, approved: 0, pending: 0, rejected: 0 },
      ),
    [summaryRows],
  );
  const structuredConflict = useMemo(
    () => parseStructuredConflict(startExtraction.error),
    [startExtraction.error],
  );
  const running =
    startExtraction.isPending ||
    job.data?.status === "queued" ||
    job.data?.status === "running";
  const done = summaryEnabled && !running && !terminalFailed;
  const emptySuccess = done && !summary.isPending && totals.total === 0;
  const progressValue = running ? job.data?.progress ?? 0 : done ? 100 : 0;
  const groupedErrors = groupErrors(job.data);
  const sourceLookup = useMemo(() => selectedSourceMap(selectedSources.data), [selectedSources.data]);
  const labelSelectionDisabled = running || startExtraction.isPending;
  const selectedLabelSet = useMemo(() => new Set(selectedLabels), [selectedLabels]);
  const selectedLabelCount = selectedLabels.length;

  const pendingSources = useMemo(() => {
    if (!conflict) return [];

    if (conflict.sources.length > 0) {
      const map = new Map<string, PendingSource>();
      for (const source of conflict.sources) {
        if (!source.source_id) continue;
        const selected = sourceLookup.get(source.source_id);
        map.set(source.source_id, {
          source_id: source.source_id,
          citation_number: source.citation_number ?? selected?.citation_number,
          title: source.title ?? selected?.title,
          url: source.url ?? selected?.url,
        });
      }
      return Array.from(map.values());
    }

    return (
      selectedSources.data
        ?.filter(
          (source) =>
            source.selected &&
            (source.citation_number == null || source.abnt_reference == null),
        )
        .map((source) => ({
          source_id: source.id,
          citation_number: source.citation_number,
          title: source.title,
          url: source.url,
        })) ?? []
    );
  }, [conflict, sourceLookup, selectedSources.data]);

  useEffect(() => {
    if (!startExtraction.isError) {
      setConflict(null);
      return;
    }
    setConflict(structuredConflict);
  }, [startExtraction.isError, structuredConflict]);

  useEffect(() => {
    if (!job.isTerminal || !jobId) return;
    qc.invalidateQueries({ queryKey: ["project", project.id] });
    qc.invalidateQueries({ queryKey: ["extractions", project.id] });
  }, [job.isTerminal, jobId, project.id, qc]);

  const start = () => {
    setConflict(null);
    startExtraction.mutate({ labels: selectedLabels }, {
      onSuccess: (data) => setJobId(data.job_id),
    });
  };

  const rebuildReferences = async () => {
    await buildReferences.mutateAsync();
    start();
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={4}
        eyebrow="Etapa 04 - Pipeline"
        title="Extracao e classificacao"
        lede="O sistema baixa as fontes, extrai texto, segmenta em chunks, aplica pre-filtro lexico e classifica cada trecho em uma das dezesseis labels. Sem traducao nesta etapa."
      />

      {!running && !done && !terminalFailed && (
        <div className="grid grid-cols-12 gap-10 mb-12 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="display-2 mb-3">Pronto para extrair.</div>
            <p className="lede">
              {selectedSources.isPending
                ? "Carregando fontes selecionadas..."
                : `${selectedCount} fontes selecionadas serao processadas para ${selectedLabelCount} label(s).`}
            </p>
            <LabelSelector
              className="mt-8"
              selected={selectedLabelSet}
              disabled={labelSelectionDisabled}
              onToggle={(label) => {
                setSelectedLabels((current) =>
                  current.includes(label)
                    ? current.filter((item) => item !== label)
                    : [...current, label],
                );
              }}
              onSelectAll={() => setSelectedLabels(ALL_LABEL_KEYS)}
              onClear={() => setSelectedLabels([])}
            />
            {startExtraction.isError && (
              <div className="mt-6">
                {conflict !== null ? (
                  <Callout tone="strike" icon={Icon.AlertCircle} title="Referencias pendentes">
                    <p>{conflict.reason}</p>
                    {pendingSources.length > 0 ? (
                      <div className="mt-4">
                        <div className="caption mb-2">Fontes sem ABNT:</div>
                        <ol className="space-y-2">
                          {pendingSources.map((source, index) => {
                            const text = [source.title, source.url, source.source_id]
                              .filter(
                                (value): value is string =>
                                  typeof value === "string" && value.length > 0,
                              )
                              .join(" - ");
                            return (
                              <li
                                key={`${source.source_id}-${index}`}
                                className="border border-[var(--rule-2)] bg-[var(--paper)] px-3 py-2 rounded-sm"
                              >
                                <div className="text-[13px] leading-snug text-[var(--ink)]">
                                  {text}
                                </div>
                                <div className="caption">
                                  {source.citation_number == null
                                    ? "Citacao ausente"
                                    : `Citacao: ${source.citation_number}`}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    ) : null}
                    {selectedCount > 0 ? (
                      <div className="mt-4">
                        <Button
                          variant="line"
                          size="sm"
                          icon={buildReferences.isPending ? Icon.Loader : Icon.FileText}
                          disabled={buildReferences.isPending}
                          onClick={() => {
                            void rebuildReferences();
                          }}
                        >
                          Reconstruir referencias
                        </Button>
                      </div>
                    ) : null}
                  </Callout>
                ) : (
                  <Callout tone="strike" icon={Icon.AlertCircle} title="Extracao nao iniciada">
                    {errorDetail(startExtraction.error)}
                  </Callout>
                )}
              </div>
            )}
            {buildReferences.isError && (
              <div className="mt-6">
                <Callout
                  tone="strike"
                  icon={Icon.AlertCircle}
                  title="Referencias nao reconstruidas"
                >
                  Nao foi possivel reconstruir as referencias ABNT. Tente novamente.
                </Callout>
              </div>
            )}
          </div>
          <div className="col-span-12 lg:col-span-4 flex justify-end">
            <Button
              variant="ink"
              size="lg"
              icon={startExtraction.isPending ? Icon.Loader : Icon.Play}
              disabled={
                startExtraction.isPending ||
                selectedSources.isPending ||
                selectedCount === 0 ||
                selectedLabelCount === 0 ||
                conflict !== null ||
                buildReferences.isPending
              }
              onClick={start}
            >
              Iniciar extracao
            </Button>
          </div>
        </div>
      )}

      {running && (
        <div className="anim-rise border border-[var(--ink)] p-10 bg-[var(--paper-2)] mb-10">
          <div className="flex items-baseline justify-between mb-6">
            <div className="flex items-baseline gap-3">
              <Spinner />
              <div className="display-4 leading-none">Extraindo trechos...</div>
            </div>
            <Stamp tone="ink" className="rotate-2">
              Em execucao
            </Stamp>
          </div>
          <Progress
            value={progressValue}
            label="Pipeline"
            sub={
              job.data?.progress_message ??
              `${jobMeta.sources_processed ?? 0}/${jobMeta.total_sources ?? selectedCount} fontes processadas`
            }
          />
          <ol className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-0 border-t border-[var(--rule)]">
            {STAGES.map((stage, i) => {
              const completed = progressValue >= stage.pct;
              const prev = STAGES[i - 1];
              const active = !completed && (i === 0 || (prev != null && progressValue >= prev.pct));
              return (
                <li
                  key={stage.label}
                  className={
                    "px-4 py-4 border-b md:border-b-0 md:border-r last:border-r-0 border-[var(--rule)] " +
                    (active ? "bg-[var(--paper)]" : "")
                  }
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="step-num text-[18px] tabular text-[var(--ink-4)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {completed && (
                      <Icon.Check size={12} strokeWidth={3} className="text-[var(--ok)]" />
                    )}
                    {active && <span className="w-1.5 h-1.5 bg-[var(--ink)] rounded-full blink" />}
                  </div>
                  <div
                    className={
                      "text-[12.5px] leading-snug " +
                      (completed || active ? "text-[var(--ink)]" : "text-[var(--ink-3)]")
                    }
                  >
                    {stage.label}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {terminalFailed && (
        <div className="border border-[var(--strike)] p-8 bg-[var(--paper-2)] mb-10">
          <Stamp tone="strike">Falhou</Stamp>
          <div className="display-3 mt-4 mb-3">Extracao interrompida.</div>
          <p className="lede mb-6">
            {job.data?.error_message ?? "O job falhou antes de concluir a etapa de classificacao."}
          </p>
          <ErrorGroups groups={groupedErrors} />
          <div className="mt-6 flex justify-end">
            <Button
              variant="ink"
              size="lg"
              icon={startExtraction.isPending ? Icon.Loader : Icon.Play}
              disabled={
                startExtraction.isPending ||
                selectedSources.isPending ||
                selectedCount === 0 ||
                selectedLabelCount === 0 ||
                buildReferences.isPending
              }
              onClick={start}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {done && (
        <>
          <div className="grid grid-cols-12 gap-10 items-end mb-12">
            <div className="col-span-12 lg:col-span-8">
              <div className="flex items-baseline gap-3 mb-3">
                <Stamp tone={emptySuccess ? "strike" : "ok"}>
                  {emptySuccess ? "Sem trechos" : "Concluida"}
                </Stamp>
              </div>
              <div className="display-2 mb-2">
                {emptySuccess ? "Nenhum trecho aproveitavel foi salvo." : "Extracao concluida."}
              </div>
              <p className="lede">
                {emptySuccess
                  ? "O pipeline terminou, mas os filtros e validacoes nao salvaram trechos utilizaveis para revisao."
                  : `${totals.total} trechos extraidos a partir de ${selectedCount} fontes, classificados em 16 labels.`}
              </p>
              {groupedErrors.length > 0 && (
                <div className="mt-6">
                  <ErrorGroups groups={groupedErrors} />
                </div>
              )}
              <LabelSelector
                className="mt-8"
                selected={selectedLabelSet}
                disabled={labelSelectionDisabled}
                onToggle={(label) => {
                  setSelectedLabels((current) =>
                    current.includes(label)
                      ? current.filter((item) => item !== label)
                      : [...current, label],
                  );
                }}
                onSelectAll={() => setSelectedLabels(ALL_LABEL_KEYS)}
                onClear={() => setSelectedLabels([])}
              />
            </div>
            <div className="col-span-12 lg:col-span-4 flex flex-col items-end gap-4">
              <div className="grid grid-cols-3 gap-5 border-l border-[var(--rule)] pl-6 text-right">
                <Stat value={totals.approved} label="Aprov." size="md" />
                <Stat value={totals.pending} label="Pend." size="md" />
                <Stat value={totals.rejected} label="Rej." size="md" />
              </div>
              <Button
                variant="ink"
                size="lg"
                iconRight={Icon.ArrowRight}
                disabled={emptySuccess}
                onClick={() => onAdvance(5)}
              >
                Visao geral (EN)
              </Button>
              <Button
                variant="line"
                size="sm"
                icon={startExtraction.isPending ? Icon.Loader : Icon.RefreshCw}
                disabled={
                  startExtraction.isPending ||
                  selectedSources.isPending ||
                  selectedCount === 0 ||
                  selectedLabelCount === 0 ||
                  buildReferences.isPending
                }
                onClick={start}
              >
                Extrair labels selecionadas
              </Button>
            </div>
          </div>

          <Rule className="mb-5">Secao 4.1 - Resumo por label</Rule>
          {summary.isPending ? (
            <div className="py-16 flex items-center justify-center gap-3 caption">
              <Spinner /> Carregando resumo...
            </div>
          ) : (
            <table className="w-full border-t border-[var(--rule)]">
              <thead>
                <tr className="caption text-left">
                  <th className="py-2 px-3 font-normal w-12 border-b border-[var(--rule)]">N</th>
                  <th className="py-2 px-3 font-normal border-b border-[var(--rule)]">Label</th>
                  <th className="py-2 px-3 font-normal text-right border-b border-[var(--rule)] w-24">Aprov.</th>
                  <th className="py-2 px-3 font-normal text-right border-b border-[var(--rule)] w-24">Pend.</th>
                  <th className="py-2 px-3 font-normal text-right border-b border-[var(--rule)] w-24">Rej.</th>
                  <th className="py-2 px-3 font-normal text-right border-b border-[var(--rule)] w-24">Total</th>
                  <th className="py-2 px-3 font-normal w-32 border-b border-[var(--rule)]">Distribuicao</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row, i) => {
                  const total = row.total || 1;
                  return (
                    <tr key={row.label} className="border-b border-[var(--rule-2)] hover:bg-[var(--paper-2)]">
                      <td className="py-2.5 px-3 mono text-[11px] tabular text-[var(--ink-3)]">
                        {String(i + 1).padStart(2, "0")}
                      </td>
                      <td className="py-2.5 px-3 text-[13.5px]">
                        <div>{row.title_pt}</div>
                        <div className="caption mt-1">{row.label}</div>
                      </td>
                      <td className="py-2.5 px-3 text-right mono text-[12.5px] tabular text-[var(--ok)]">{row.approved}</td>
                      <td className="py-2.5 px-3 text-right mono text-[12.5px] tabular text-[var(--ink-2)]">{row.pending}</td>
                      <td className="py-2.5 px-3 text-right mono text-[12.5px] tabular text-[var(--strike)]">{row.rejected}</td>
                      <td className="py-2.5 px-3 text-right mono text-[13px] tabular text-[var(--ink)]">{row.total}</td>
                      <td className="py-2.5 px-3">
                        <div className="flex h-2 w-full bg-[var(--rule-2)]">
                          <span className="bg-[var(--ok)]" style={{ width: `${(row.approved / total) * 100}%` }} />
                          <span className="bg-[var(--ink)]" style={{ width: `${(row.pending / total) * 100}%` }} />
                          <span className="bg-[var(--strike)]" style={{ width: `${(row.rejected / total) * 100}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function LabelSelector({
  selected,
  disabled,
  onToggle,
  onSelectAll,
  onClear,
  className = "",
}: {
  selected: Set<string>;
  disabled: boolean;
  onToggle: (label: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <Rule>Labels da extracao</Rule>
          <p className="caption mt-2">
            Selecione uma, varias ou todas as labels para processar novamente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="line" size="sm" disabled={disabled} onClick={onSelectAll}>
            Todas
          </Button>
          <Button variant="ghost" size="sm" disabled={disabled} onClick={onClear}>
            Limpar
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 border-t border-l border-[var(--rule)]">
        {LABELS_16.map((label) => {
          const checked = selected.has(label.key);
          return (
            <button
              type="button"
              key={label.key}
              disabled={disabled}
              aria-pressed={checked}
              onClick={() => onToggle(label.key)}
              className={
                "min-h-[92px] text-left p-4 border-r border-b border-[var(--rule)] transition-colors " +
                (checked ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] hover:bg-[var(--paper-2)]")
              }
            >
              <div className="flex items-start gap-3">
                <span
                  className={
                    "mt-1 w-4 h-4 border flex items-center justify-center shrink-0 " +
                    (checked ? "border-[var(--paper)]" : "border-[var(--ink-3)]")
                  }
                >
                  {checked && (
                    <Icon.Check size={11} strokeWidth={3} className="text-[var(--paper)]" />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] leading-tight">{label.title_pt}</span>
                  <span
                    className={
                      "block mono text-[10px] leading-snug mt-1 " +
                      (checked ? "text-[var(--paper)] opacity-70" : "text-[var(--ink-3)]")
                    }
                  >
                    {label.title_en}
                  </span>
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ErrorGroups({ groups }: { groups: GroupedError[] }) {
  if (groups.length === 0) return null;
  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key} className="border border-[var(--rule)] bg-[var(--paper)] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Tag tone="strike">{group.stage}</Tag>
              <span className="caption">{group.count} ocorrencia(s)</span>
            </div>
            <span className="mono text-[11px] text-[var(--ink-3)]">
              {String(group.items[0]?.source_id ?? "")}
            </span>
          </div>
          <div className="mt-3 text-[13px] leading-relaxed text-[var(--ink-2)]">
            {String(group.items[0]?.error ?? "Falha registrada sem detalhe.")}
          </div>
        </div>
      ))}
    </div>
  );
}
